
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import DataTable from '../../../shared/tables/DataTable';
import SummaryCard from '../../../shared/cards/SummaryCard';
import { getOrderSummaryRequest, getOrderHistoryRequest, getOrderDownloadRequest } from '../../../redux/actions/ordersPageActions';
import { getTransactionDetailsRequest } from '../../../redux/actions/transactionActions';
import CustomPagination from '../../../shared/pagination/CustomPagination';
import DateRangeButton from '../../../shared/datePicker/DateRangeButton';
import './OrdersPage.scss';
import DownloadIcon from '../../../../public/assets/img/download.svg';
import { orderSummaryDataSaga, orderHistoryDataSaga, orderHistoryDataErrorSaga } from '../../../redux/saga/ordersPageSelectors';
import { selectedStoreSelectorSaga } from '../../../redux/saga/storePageSelectors';
import { DOWNLOAD_REPORT_TEXT, DOWNLOAD_TEXT, ORDER_TEXT } from '../../../constants/commonConstants';
import { TABLE_NAME, TABLE_SUBTITLE, STORES_PAGE_PLACEHOLDER, ORDER_HISTORY_COLUMNS, ORDER_SUMMARY_COLUMNS, DOWNLOAD_OPTIONS, DOWNLOAD_REPORT, CHECKBOX_SELECTOR, SUB_TITLE, RADIO_SELECTOR, NO_PREVIOUS_ORDER_HISTORY } from '../../../constants/ordersPageConstants';
import { endDateRangeSaga, startDateRangeSaga } from '../../../redux/saga/dateRangeSelector';
import OrderDetailsCard from '../../../shared/cards/OrderDetailsCard';
import HelpPopOver from '../../../shared/pop-over/help.pop-over';
import DownloadSelectorPopover from '../../../shared/pop-over/download-selector.pop-over';
import { IconButtonComponent } from '../../../shared/iconbutton/iconButton';
import { handleSortingInTable } from '../../../shared/utils/format.helpers';
import { setDateRange } from '../../../redux/actions/dateRangeActions';
import { useMerchant } from '../../../MerchantContext';
import { accessTokenSaga } from '../../../redux/saga/loginServiceSelector';
import { BUTTON_CLICK, DOWNLOAD_MENU_CLOSED, DOWNLOAD_MENU_OPENED, DOWNLOAD_REPORT_BUTTON, N_A, ORDER_PAGE } from '../../../constants/mixPannelConstants';
import { updateMixPanel } from '../../../redux/actions/merchantAction';
import WarningCard from '../../../shared/cards/WarningCard';

export default function OrdersPage() {

	const orderSummaryData = useSelector(orderSummaryDataSaga);
	const orderHistoryData = useSelector(orderHistoryDataSaga);
	const orderHistoryDataError = useSelector(orderHistoryDataErrorSaga);
	const selectedStore = useSelector(selectedStoreSelectorSaga);
	const accessToken = useSelector(accessTokenSaga);

	//let noResultsText = ''
	//if (orderHistoryDataError || orderHistoryData?.orders?.totalElements == 0) {
	//	noResultsText = orderHistoryDataError.errorMessage;
	//}

	const [currentPageNumber, setCurrentPageNumber] = useState(0);
	const [numberOfElementsonThisPage, setNumberOfElementsonThisPage] = useState(10);
	const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
	const [totalNumberOfElements, setTotalNumberOfElements] = useState(0);
	const [openDetailsPanel, setOpenDetailsPanel] = useState(false);
	const [openHelpPopOver, setOpenHelpPopOver] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState(null);
	const [storeOffset, setStoreOffset] = useState('');
	const [openDownloadPopOver, setOpenDownloadPopOver] = useState(false);
	const dispatch = useDispatch();
	let startDate = useSelector(startDateRangeSaga);
	let endDate = useSelector(endDateRangeSaga);
	if (startDate === null && endDate === null) {
		startDate = sessionStorage.getItem('startDate');
		endDate = sessionStorage.getItem('endDate');
		dispatch(setDateRange(startDate, endDate));
	}
	const [searchTerm, setSearchTerm] = useState('');
	const [isOneStoreSelected, setisOneStoreSelected] = useState(false);

	const { merchantId, setMerchantId } = useMerchant();
	const [storeIds, setStoreIds] = useState([]);
	//using token from here now until we get it the api to get this figured
	const token =process.env.PAYMENT_AUTHORIZATION_TOKEN ;
	const [sortConfig, setSortConfig] = useState({ key: 'orderDate', direction: 'Descending' });

	const handleDetailsPanelClose = () => {
		setOpenDetailsPanel(false)
	}

	const handleSort = (columnKey) => {
		handleSortingInTable(columnKey, sortConfig, setSortConfig );
	};

	// Handlers for pagination changes
	const handleChangePage = (event, newPage) => {
		setCurrentPageNumber(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setNumberOfElementsonThisPage(parseInt(event.target.value, 10));
		setCurrentPageNumber(0);
	};

	useEffect(() => {
		// console.log('SELECTED STORE ',selectedStore)
		if (selectedStore) {
			setStoreIds(selectedStore.storeId);
			if(selectedStore.storeId.length === 1) {
				setisOneStoreSelected(true);
			} else {
				setisOneStoreSelected(false);
			}
		}
	}, [selectedStore]);

	useEffect(() => {
		if( startDate && endDate && startDate.includes('-') && endDate.includes('-') && merchantId && accessToken ) {
			dispatch(getOrderSummaryRequest(
				startDate,
				endDate,
				merchantId,
				storeIds
			));
			setCurrentPageNumber(0);
		}
	}, [dispatch, startDate, endDate, storeIds, merchantId, accessToken]);

	useEffect(() => {
		// Dispatch the action to fetch order history
		if ( startDate && endDate && startDate.includes('-')  && endDate.includes('-')  && merchantId !==null && accessToken !== null ) {
			dispatch(getOrderHistoryRequest(
				startDate,
				endDate,
				merchantId,
				searchTerm,
				{
					storeIds: storeIds,
					pageNum: currentPageNumber,
					pageSize: numberOfElementsonThisPage,
					sortBy: sortConfig.key,
					sortOrder: sortConfig.direction,
					isPayoutScreen: false,
				}
			));
		}
	}, [dispatch, currentPageNumber, numberOfElementsonThisPage, sortConfig, startDate, endDate, searchTerm, storeIds, merchantId, accessToken]);

	const enrichedOrderHistoryData = useMemo(() => {
		if (orderHistoryData && orderHistoryData.orders && Array.isArray(orderHistoryData.orders.content)) {
			const storeOffsetMap = orderHistoryData.storeOffsetMap;
			setStoreOffset(storeOffsetMap);
			return orderHistoryData.orders.content.map(order => {
				// Metadata enrichment
				//const transactionDetailContainer = transactionDetails.find(detailContainer => detailContainer.transactionId === order.transactionId);
				//const metadata = transactionDetailContainer && transactionDetailContainer.data && transactionDetailContainer.data.metadata;
				//const customerName = metadata ? `${metadata.first_name} ${metadata.last_name}` : order.customerName;

				// Timezone adjustment
				const offsetHours = parseInt(storeOffsetMap[order.storeUUID], 10);
				if(order?.orderDateWithOffset) {
					return {
						...order,
						orderDate: order.orderDateWithOffset,
					};
				} else {
					let adjustedOrderDate = order.orderDate;
					if (!isNaN(offsetHours)) {
						const orderDateTime = new Date(order.orderDate);
						const timezoneAdjustedDate = new Date(orderDateTime.setHours(orderDateTime.getHours() + offsetHours));
						adjustedOrderDate = timezoneAdjustedDate.toUTCString();
					}
					// Return the enriched and adjusted order object
					return {
						...order,
						//customerName: customerName,
						orderDate: adjustedOrderDate,
					};
				}
			});
		}
		return [];
	}, [orderHistoryData]);

	useEffect(() => {
		if (orderHistoryDataError || !orderHistoryData || !orderHistoryData.orders) {
			setTotalNumberOfPages(0);
			setTotalNumberOfElements(0);
		} else {
			// If data is available and there's no error, update states based on the data
			setTotalNumberOfPages(orderHistoryData.orders.totalPages || 0);
			setTotalNumberOfElements(orderHistoryData.orders.totalElements || 0);
		}
	}, [orderHistoryData, orderHistoryDataError]);

	const handleCellClick = (rowData, columnKey) => {
		if (rowData.merchantOrderId) {
			setSelectedRowData(rowData);
			setOpenDetailsPanel(true)
			dispatch(getTransactionDetailsRequest(rowData.merchantOrderId, token));
		}
	};

	const handleHelpPopOverClose = () => {
		setOpenHelpPopOver(false)
	}

	const handleOpenHelp = () => {
		setOpenHelpPopOver(true)
	}


	const handleDownloadPopOverClose = (data) => {
		if (data?.value?.length > 0) {
			dispatch(getOrderDownloadRequest(
				startDate,
				endDate,
				merchantId,
				'',
				{
					storeIds: [],
					pageNum: 0,
					pageSize: null,
					sortBy: 'orderDate',
					sortOrder: 'Ascending',
					isPayoutScreen: false,
				},
				data?.value
			));
		}
		setOpenDownloadPopOver(false);
		dispatch(updateMixPanel({type: BUTTON_CLICK, value: {
			buttonName: DOWNLOAD_REPORT_BUTTON,
			outcome: DOWNLOAD_MENU_CLOSED,
			optionselected: data?.value[0].label,
			EventTime: new Date().toUTCString(),
			PageName: ORDER_PAGE,
			timeSpent: N_A
		}, check: 'no'}));
	};

	const downloadReciptHanlder = () => {
		setOpenDownloadPopOver(true);
		dispatch(updateMixPanel({type: BUTTON_CLICK, value: {
			buttonName: DOWNLOAD_REPORT_BUTTON,
			outcome: DOWNLOAD_MENU_OPENED,
			EventTime: new Date().toUTCString(),
			PageName: ORDER_PAGE,
			timeSpent: N_A
		}, check: 'no'}));
	}

	return (
		<>
			<div className='orders-container'>
				<Paper className='page-header'>
					<h4 className='title' data-testid='orders-title' >{ORDER_TEXT}</h4>

					<div className='form-container--options-cont'>
						<DateRangeButton pageName={ORDER_PAGE}/>
						<IconButtonComponent icon={DownloadIcon} buttonText={DOWNLOAD_REPORT_TEXT} clickEvent={downloadReciptHanlder}/>
					</div>


				</Paper>
			</div>
			<div className='warning-message-top'>
				<WarningCard />
			</div>
			<div className='orders-page'>
				<div className='summaryCard'>
					{orderSummaryData ? (
						<SummaryCard
							summaryRows={orderSummaryData}
							summaryColumns={ORDER_SUMMARY_COLUMNS}
							dollarColumnValues={['orders', 'avgOrderValue', 'totalPayout']}
						/>
					) : (
						<SummaryCard
							summaryColumns={ORDER_SUMMARY_COLUMNS}
							dollarColumnValues={['orders', 'avgOrderValue', 'totalPayout']}
						/>
					)}
				</div>
				<div className='data-table'>
					<DataTable
						data={enrichedOrderHistoryData}
						columns={ORDER_HISTORY_COLUMNS}
						tableName={TABLE_NAME}
						tableSubTitle={TABLE_SUBTITLE}
						showAddToList={false}
						excludeColumnKey='merchantOrderId'
						clickableColumnKey='merchantOrderId'
						includeCheckbox={false}
						searchplaceholder={STORES_PAGE_PLACEHOLDER}
						onSort={handleSort}
						sortConfig={sortConfig}
						onSearch={(searchTerm) => {
							setSearchTerm(searchTerm);
							setCurrentPageNumber(0);
						}}
						onCellClick={handleCellClick}
						noResultsText={NO_PREVIOUS_ORDER_HISTORY}
						uncheckTheseFilters={['storeDetails']}
						isOneStoreSelected={isOneStoreSelected}
					/>
					<CustomPagination
						totalNumberOfPages={totalNumberOfPages}
						currentPageNumber={currentPageNumber}
						rowsPerPage={numberOfElementsonThisPage}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 20, 50]}
					/>

				</div>
			</div>

			<OrderDetailsCard
				selectedRow={selectedRowData}
				open={openDetailsPanel}
				storeOffset={storeOffset}
				onClose={(value) => handleDetailsPanelClose(value)}
				openHelp={() => handleOpenHelp()}
			/>
			<HelpPopOver
				open={openHelpPopOver}
				onClose={(value) => handleHelpPopOverClose(value)}
			></HelpPopOver>
			<DownloadSelectorPopover
				title={DOWNLOAD_REPORT}
				subTitle={SUB_TITLE}
				open={openDownloadPopOver}
				listData={DOWNLOAD_OPTIONS}
				listControl={RADIO_SELECTOR}
				listSelectAll={false}
				onClose={(value) => handleDownloadPopOverClose(value)} />
		</>
	)
}
